import React, { useState, useMemo } from 'react';
import clsx from 'clsx';

import { DefaultSm, MobileSm } from 'components/MediaQueries';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import './select.scss';

const DropdownInput = ({
  isOpen,
  onClick,
  setRef = null, // Used to stop click propagation in parent element.
  ...props
}) => {

  return (
    <input
      ref={setRef}
      type="text"
      onClick={onClick}
      {...props}
    />
  );
};

const Select = (props) => {
  const {
    options = null,
    showSearch,
    children,
    className,
    onChange,
    value,
    showError,
    emptyValue = 'Select...',
    disabled = false,
    responsive = true,
    dropdownButtonProps,
    footer,
    fitModal,
    useDismissProps,
    ...rest
  } = props;

  // This broke the project settings web component selects, but leaving here in case it was important
  // for something else.
  // const _useDismissProps = useDismissProps || { outsidePressEvent: 'click' };
  const _useDismissProps = useDismissProps;

  const [searchKey, setSearchValue] = useState('');

  const [isOpen, setIsOpened] = useState(false);
  // #TODO: This memo is firing whenever the form is changed.
  const optionState = useMemo(() => {
    return children.flat().reduce((o, child) => {
      if (child.type === 'option') {
        return ({ ...o, [child.props.value]: child.props.children });
      }
      // #TODO: Refactor this to be recursive.
      if (child.type === 'optgroup') {
        const ogOptions = child.props.children.flat().reduce((o1, child1) => {
          if (child1.type === 'option') {
            return ({ ...o1, [child1.props.value]: child1.props.children });
          }
          return {};
        }, {});
        return ({ ...o, ...ogOptions });
      }
      return {};
    }, {});
  }, [children]);

  const show = () => {
    setIsOpened(true);
  };

  const dismiss = (e) => {
    if (!showSearch) {
      setIsOpened(false);
    }
    // Prevent double click on input from dismissing dropdown.
    else if (isOpen && e?.target.nodeName !== 'INPUT') {
      setIsOpened(false);
    }
  };

  const handleDropdownClick = (e) => {
    setSearchValue('');
    dismiss();
    onChange(e);
  };

  const triggerField = () => {
    const emptyPlaceholder = isOpen ? 'Enter search terms' : 'Select...';
    const placeholder = isOpen && value ? optionState[value] : emptyPlaceholder;
    return (
      <DropdownInput
        className={`ap-dropdown__trigger-search-input select ${clsx(showError && 'select-error')}`}
        value={isOpen ? searchKey : optionState[value] ?? ''}
        placeholder={placeholder}
        onChange={(e) => {
          setSearchValue(e.target.value);
        }}
        disabled={disabled}
      />
    );
  };

  const { className: buttonClasses, ...restButtonProps } = dropdownButtonProps || {};

  const TriggerButton = (
    <DropdownButton
      {...restButtonProps}
      className={clsx(
        'select',
        'button--unstyled',
        showError && 'select-error',
        buttonClasses,
      )}
      disabled={disabled}
    >
      {value && value !== 0 && value !== '0' ? optionState[value] : emptyValue}
    </DropdownButton>
  );

  const filterChildren = (opt, child) => {
    if (child.type === 'option') {
      if (child.props.children.toLowerCase().includes(searchKey.toLowerCase()))
        return ([...opt, child]);
    }
    if (child.type === 'optgroup') {
      const ogOptions = child.props.children.flat().reduce((opt1, child1) => {
        return filterChildren(opt1, child1);
      }, []);
      const childFiltered = React.cloneElement(child, { children: ogOptions });
      if (childFiltered.props.children.length)
        return ([...opt, childFiltered]);
    };
    return opt;
  };

  const filterChildrens = (childOpt) => {
    return childOpt.flat().reduce((opt, child) => {
      return filterChildren(opt, child);
    }, []);
  };

  const convert = (child, index) => {
    const key = child.key ?? `${child.type}--${index}`;
    switch (child.type) {
      case 'option':
        return (
          <button
            key={key}
            onClick={handleDropdownClick}
            className="button--unstyled option"
            {...child.props}
          />
        );
      case 'optgroup':
        return (
          <div
            key={key}
            className="optgroup"
          >
            <label className="optgroup__label">{child.props.label}</label>
            {child.props.children.flat().map(c => convert(c))}
          </div>
        );
      default:
        return child;
    };
  };

  const convertedChildren = (showSearch && searchKey ? filterChildrens(children) : children)
    .flat().map((child, index) => {
      return convert(child, index);
    });

  const EmptyOptions = (
    <button
      className="button--unstyled option"
      disabled
    >
      <em>No options found</em>
    </button>
  );

  return (
    <div className="ap-select">
      {responsive && !footer ? (
        <>
          <MobileSm>
            <select
              value={value}
              onChange={onChange}
              {...rest}
            >
              <option value="">
                Select...
              </option>
              {options ? (
                options.map(({ value: v, label }) => {
                  return (
                    <option
                      key={`ap-select--${v}`}
                      value={v}
                    >
                      {label}
                    </option>
                  );
                })
              ) : (
                <>
                  {children}
                </>
              )}
            </select>
          </MobileSm>
          <DefaultSm>
            {/* #TODO: Need to refactor this into a component so it's not duplicated below */}
            <APDropdown
              isOpen={isOpen}
              show={show}
              dismiss={dismiss}
              triggerButton={showSearch ? triggerField() : TriggerButton}
              className={clsx(className)}
              root={null}
              dropDownClass={
                showSearch
                  ? 'ap-dropdown__menu--extended'
                  : fitModal
                  ? 'ap-dropdown__menu--fit-modal'
                  : ''
              }
              menuClass="select-menu"
              isFullWidth
              placement='bottom-start'
              useDismissProps={_useDismissProps}
              disabled={disabled}
              {...rest}
            >
              {convertedChildren.length > 0 ? <>{convertedChildren}</> : EmptyOptions}
            </APDropdown>
          </DefaultSm>
        </>
      ) : (
        <APDropdown
          isOpen={isOpen}
          show={show}
          dismiss={dismiss}
          triggerButton={showSearch ? triggerField() : TriggerButton}
          className={clsx(className)}
          root={null}
          dropDownClass={
            showSearch
              ? 'ap-dropdown__menu--extended'
              : fitModal
              ? 'ap-dropdown__menu--fit-modal'
              : ''
          }
          menuClass="select-menu"
          isFullWidth
          footer={footer}
          placement='bottom-start'
          useDismissProps={_useDismissProps}
          disabled={disabled}
          {...rest}
        >
          {convertedChildren.length > 0 ? (
            <>{convertedChildren}</>
          ) : (
            EmptyOptions
          )}
        </APDropdown>
      )}
      {!disabled ? (
        <span className="fa fa-chevron-down select-icon" />
      ) : null }
    </div>
  );
};

export default Select;
